import * as React from "react";
import {css, cx} from "emotion";
import {rgba} from "polished";
import {faShoppingCart} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}  from "@fortawesome/react-fontawesome";

import "./right-nav.scss";

import {ComponentLink, ComponentNext, makeChain, valueHelper} from "@Common/react/cst2";
import {UseState}                                             from "@Common/react/use-state";
import {AlertModal}                                           from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";
import {chain}                                                from "@Common/utils/fs";

import {consumeContext} from "@Xpresso/loaders/consume-context";

import {MenuInternalLink} from "../../../../common/components/internal-link";
import {AGENT_LEVEL}      from "../../../../common/logic/user-level";

import {BranchSwitcher}   from "./branch-switcher/branch-switcher";
import {EmulationControl} from "./emulation-control/emulation-control";
import {helpMenu}         from "./help-menu/help-menu";
import {navAccount}       from "./nav-account/nav-account";
import {SearchInput}      from "./search-input/search-input";

type Open = {area: string, id: string, value: boolean};

export const RightNav = ({
    disableSuggestionsDialog=false,
    showEmulation,
    showBranchSwitcher,
    emulationPlugins,
    navBlock=undefined,
}) => (
    makeChain()
    .then(...consumeContext("cart"))
    .then(...consumeContext("company"))
    .then(...consumeContext("menus"))
    .then(...consumeContext("themeScheme"))
    .then(...consumeContext("userLevels"))
    .then("defaultOpenSearch", valueHelper(({company, userLevels}) =>
    {
        const userIsAgent           = userLevels?.userLevel === AGENT_LEVEL;
        const isOmniSearchEnabled   = company?.featureFlags?.omniSearch?.value || false;
        return isOmniSearchEnabled && ((!showBranchSwitcher && !showEmulation) || userIsAgent);
    }))
    .then("open", ({defaultOpenSearch}, next:ComponentLink<typeof UseState<Open>>) => (
        UseState({next, initValue: {area: "right", id: "search", value: defaultOpenSearch}})
    ))
    .then("alertModal", ({}, next:ComponentLink<typeof AlertModal>) => AlertModal({next}))
    .then(({alertModal, cart, defaultOpenSearch, menus, open, themeScheme}) => (menus || themeScheme.helpMenu) && (
        <div
            className={cx("right-nav right-nav-3eq", css`
                &:after {
                    background: linear-gradient(to right, ${rgba(themeScheme.xpresso.menuBackgroundColor, 0)}, ${rgba(themeScheme.xpresso.menuBackgroundColor, 1)});
                }
            `)}
            style={{background: themeScheme.xpresso.menuBackgroundColor,}}
        >

            {showEmulation && (
                EmulationControl({
                    hidden: open.value?.area === "right" && open.value.value && ~["search", "profile"].indexOf(open.value.id),
                    emulationPlugins,
                })
            )}

            {showBranchSwitcher && (
                BranchSwitcher({
                    hidden: open.value?.area === "right" && open.value.value && ~["search", "profile"].indexOf(open.value.id),
                })
            )}

            {menus && SearchInput({
                open: scopeOpen(open, "right", "search", defaultOpenSearch),
                disableSuggestionsDialog,
                navBlock,
            })}

            {helpMenu({
                menu: menus?.helpMenu ?? themeScheme.helpMenu,
                open: scopeOpen(open, "right", "help", defaultOpenSearch),
                navBlock,
            })}

            {menus && (() => {
                const props = {
                    className: "cart",
                    "xd-qa-type": "icon",
                    "xd-qa-name":"cart",
                    "style": {color: themeScheme.xpresso.menuTextColor},
                    children: <>
                        {"Cart "}
                        {chain(
                            cart?.value?.items?.length,
                            (count) => count > 0 ? ` (${count})` : "(0)"
                        )}
                        <FontAwesomeIcon icon={faShoppingCart}/>
                    </>,
                };

                return cart?.value?.items?.length ? (
                    MenuInternalLink({
                        ...props,
                        navBlock,
                        to: `/checkout/${cart?.value.cartId}`,
                    })
                ) : (
                    <a
                        {...props}
                        onClick={() => alertModal.show({
                            message: "There are no orders in your cart.",
                        })}
                        title="There are no orders in your cart."
                    />
                );
            })()}

            {menus && navAccount({
                menu: menus.profileMenu,
                open: scopeOpen(open, "right", "profile", defaultOpenSearch),
                navBlock,
            })}
        </div>
    ))
    .run()
);

const scopeOpen = (
    open: ComponentNext<typeof UseState<Open>>,
    area: string,
    id: string,
    defaultOpenSearch: boolean,
) => ({
    value: (
        open.value?.area === area && open.value?.id === id
        ? open.value.value
        : false
    ),
    onChange: (value: boolean) => open.onChange(
        value || !defaultOpenSearch
        ? {area, id, value}
        : {area: "right", id: "search", value: true}
    ),
});

